import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheck, FaClock } from 'react-icons/fa';

const ServiceDetail = () => {
  const { serviceType } = useParams();
  
  // Services data
  const services = {
    portfolio: {
      title: "Portfolio Websites",
      description: "Showcase your work with stunning visual presentations and smooth interactions. Perfect for artists, photographers, and creative professionals.",
      image: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Custom-designed portfolio showcase",
        "Project categorization and filtering",
        "High-quality image galleries",
        "Mobile-responsive layout",
        "Contact form integration",
        "SEO optimization"
      ],
      timeline: "2-3 weeks",
      price: "₹8,000"
    },
    business: {
      title: "Business Websites",
      description: "Establish your professional online presence with modern, responsive business websites that drive conversions and grow your brand.",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Professional company presentation",
        "Service/product showcases",
        "Team member profiles",
        "Testimonial sections",
        "Contact forms with map integration",
        "Blog functionality"
      ],
      timeline: "3-4 weeks",
      price: "₹10,000"
    },
    ecommerce: {
      title: "E-Commerce Stores",
      description: "Sell products online with secure, user-friendly e-commerce websites featuring payment integration and inventory management.",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Product catalog with categories",
        "Shopping cart and checkout system",
        "Payment gateway integration",
        "Inventory management system",
        "Order tracking",
        "Customer accounts"
      ],
      timeline: "4-6 weeks",
      price: "₹15,000"
    },
    "personal-brand": {
      title: "Personal Brand Sites",
      description: "Build your personal brand with a website that highlights your expertise, achievements, and services to attract opportunities.",
      image: "https://images.unsplash.com/photo-1493119508027-2b584f234d6c?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Personal brand storytelling",
        "Biography and expertise highlights",
        "Service offerings",
        "Testimonials and endorsements",
        "Blog/content platform",
        "Lead generation forms"
      ],
      timeline: "2-3 weeks",
      price: "₹9,000"
    },
    booking: {
      title: "Booking/Appointment Sites",
      description: "Enable clients to book your services online with intuitive scheduling systems and automatic confirmations.",
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Interactive booking calendar",
        "Appointment scheduling system",
        "Automated email confirmations",
        "Service selection options",
        "Payment processing",
        "Admin dashboard"
      ],
      timeline: "3-4 weeks",
      price: "₹12,000"
    },
    educational: {
      title: "Educational Platforms",
      description: "Create engaging learning experiences with custom educational websites featuring course management and student tracking.",
      image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Course creation and management",
        "Lesson modules and sections",
        "Video lecture integration",
        "Quiz and assessment tools",
        "Student progress tracking",
        "Discussion forums"
      ],
      timeline: "5-7 weeks",
      price: "₹18,000"
    },
    landing: {
      title: "Landing Pages",
      description: "Maximize conversions with high-impact landing pages designed to capture leads and promote specific products or services.",
      image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Conversion-optimized design",
        "Compelling call-to-actions",
        "Lead capture forms",
        "A/B testing capability",
        "Mobile optimization",
        "Fast loading speed"
      ],
      timeline: "1-2 weeks",
      price: "₹6,000"
    },
    event: {
      title: "Event & Wedding Sites",
      description: "Celebrate special occasions with beautiful event websites that provide information and allow guests to RSVP.",
      image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Beautiful event showcase",
        "RSVP functionality",
        "Event schedule and details",
        "Venue information with maps",
        "Photo galleries",
        "Countdown timer"
      ],
      timeline: "2-3 weeks",
      price: "₹8,000"
    },
    ngo: {
      title: "NGO & Donation Platforms",
      description: "Support causes with websites that tell your organization's story and facilitate online donations for your mission.",
      image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=1200&q=80",
      features: [
        "Cause storytelling & impact reporting",
        "Online donation processing",
        "Campaign & fundraising pages",
        "Volunteer sign-up forms",
        "Event management",
        "Blog & news section"
      ],
      timeline: "3-4 weeks",
      price: "₹10,000"
    }
  };

  const service = services[serviceType];
  
  // Fallback if service doesn't exist
  if (!service) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-orbitron font-bold mb-8 text-cyber-pink">Service Not Found</h1>
          <p className="text-xl text-gray-300 mb-8">The service you're looking for doesn't exist or has been moved.</p>
          <Link to="/services" className="cyber-button">
            <span>View All Services</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="relative mb-20">
          <div className="w-full h-64 md:h-96 relative overflow-hidden rounded-xl mb-8">
            <img 
              src={service.image} 
              alt={service.title} 
              className="w-full h-full object-cover filter brightness-50"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cyber-dark via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full p-6 md:p-10">
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-4xl md:text-5xl font-orbitron font-bold mb-4 text-white"
              >
                {service.title}
              </motion.h1>
            </div>
          </div>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-300 max-w-3xl"
          >
            {service.description}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          {/* Features Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-3xl font-orbitron font-bold mb-6 text-cyber-pink">Key Features</h2>
            
            <ul className="space-y-4">
              {service.features.map((feature, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 + (index * 0.1) }}
                  className="flex items-start"
                >
                  <FaCheck className="text-cyber-neon mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300">{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
          
          {/* Timeline and Pricing */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="cyber-card p-6 mb-8">
              <h2 className="text-2xl font-orbitron font-bold mb-4 text-cyber-purple">Timeline</h2>
              <div className="flex items-center">
                <FaClock className="text-cyber-purple text-2xl mr-4" />
                <p className="text-white text-xl">{service.timeline}</p>
              </div>
            </div>
            
            <div className="cyber-card p-6">
              <h2 className="text-2xl font-orbitron font-bold mb-4 text-cyber-neon">Pricing</h2>
              <p className="text-3xl md:text-4xl font-orbitron font-bold text-white mb-2">{service.price}</p>
              <p className="text-gray-400 mb-6">Starting price</p>
              
              <Link to="/contact" className="cyber-button block w-full text-center">
                <span>Get Started</span>
              </Link>
            </div>
          </motion.div>
        </div>
        
        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="cyber-card p-8 md:p-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-orbitron font-bold mb-6">
            Ready to <span className="text-cyber-pink">Start</span> Your Project?
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Contact us today to discuss your {service.title.toLowerCase()} project and get a free consultation.
          </p>
          <Link to="/contact" className="cyber-button inline-block">
            <span>Contact Us</span>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default ServiceDetail; 